/**
 * VM password policy for the admin console.
 *
 * A second copy of the client console's `validateVmPassword` on purpose: the two frontends are
 * separate apps with no shared package, and the backend's `VmPasswordPolicy` is what actually
 * decides. Keep all three in step — a password one console accepts and the other refuses is a
 * fault nobody can reproduce.
 */
export const VM_PASSWORD_LIMITS = {
  MIN_LENGTH: 12,
  MAX_LENGTH: 64,
} as const;

/**
 * Characters refused anywhere in a VM password, beyond whitespace.
 *
 * These passwords get pasted into shells and connection strings by the people who receive them,
 * and each of these turns a working password into a support ticket there.
 */
export const VM_PASSWORD_FORBIDDEN_CHARACTERS = "'\"`\\$";

export type VmPasswordErrorCode =
  | "required"
  | "tooShort"
  | "tooLong"
  | "uppercase"
  | "lowercase"
  | "number"
  | "whitespace"
  | "forbiddenChars";

/**
 * Validate a password the operator chose for a VM.
 *
 * Returns the first failure only, in the same order the backend's cascade reports them, so the two
 * never disagree about which rule a password broke. The caller translates the code.
 */
export function validateVmPassword(password: string): {
  isValid: boolean;
  errorCode?: VmPasswordErrorCode;
} {
  if (password.length === 0) return { isValid: false, errorCode: "required" };
  if (password.length < VM_PASSWORD_LIMITS.MIN_LENGTH)
    return { isValid: false, errorCode: "tooShort" };
  if (password.length > VM_PASSWORD_LIMITS.MAX_LENGTH)
    return { isValid: false, errorCode: "tooLong" };
  if (!/[A-Z]/.test(password)) return { isValid: false, errorCode: "uppercase" };
  if (!/[a-z]/.test(password)) return { isValid: false, errorCode: "lowercase" };
  if (!/\d/.test(password)) return { isValid: false, errorCode: "number" };
  if (/\s/.test(password)) return { isValid: false, errorCode: "whitespace" };
  if ([...password].some((c) => VM_PASSWORD_FORBIDDEN_CHARACTERS.includes(c)))
    return { isValid: false, errorCode: "forbiddenChars" };

  return { isValid: true };
}

/** The rules, as one line under the input. */
export const VM_PASSWORD_HINT =
  "12–64 characters, with an upper-case letter, a lower-case letter and a number. " +
  "No spaces, and none of ' \" ` \\ $";

/** What to show when a typed password breaks a rule. */
export const VM_PASSWORD_RULE_MESSAGES: Record<VmPasswordErrorCode, string> = {
  required: "Enter a password, or let the system create one.",
  tooShort: `Password must be at least ${VM_PASSWORD_LIMITS.MIN_LENGTH} characters.`,
  tooLong: `Password must not exceed ${VM_PASSWORD_LIMITS.MAX_LENGTH} characters.`,
  uppercase: "Password must contain at least one upper-case letter.",
  lowercase: "Password must contain at least one lower-case letter.",
  number: "Password must contain at least one number.",
  whitespace: "Password must not contain spaces.",
  forbiddenChars: "Password must not contain ' \" ` \\ or $",
};
